import React, { useState, useEffect } from "react";
import { iconNew, RotaButton } from "@teamrota/rota-design";

import DateHeaders from "~/src/containers/scheduler/SchedulerGrid/DateHeaders/date-headers";
import CellRow from "~/src/containers/scheduler/SchedulerGrid/CellRow/cell-row";
import SearchInput from "~/src/containers/scheduler/SearchInput/search-input";
import AvatarCard from "~/src/containers/scheduler/AvatarCard/avatar-card";
import OpenShiftsTableRow from "~/src/containers/scheduler/OpenShifts/OpenShiftTableRow/table-row";
import ViewEditMember from "~/src/containers/modals/view-edit-member";
import styles, {
  StyledScheduleHead,
  StyledScheduleBody,
  StyledScheduleRow,
  StyledButtonWrapper,
  StyledGridHeader,
  StyledDay,
  StyledIconContainer
} from "~/src/containers/scheduler/SchedulerGrid/scheduler-grid.styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { countDraftShifts, setDraftShiftIds } from "../reducer";
import FooterLegend from "../FooterLegend";
import moment from "moment";
import useAssignMemberToShift from "~/src/graphql/mutations/assign-member-to-shift";

const { Sun, Moon } = iconNew;

const LOAD_MORE_STEP = 25;

const SchedulerGrid = ({
  schedule,
  startTime,
  startDate,
  onPressShiftCell,
  onPressGridCell,
  setHomeVenueIds,
  setRoleIds,
  setSortBy,
  setVenueIds,
  setSearchTerm,
  searchTerm,
  sortBy,
  countDraftShifts,
  setDraftShiftIds,
  totalMembersCount,
  loadMore,
  isProviderScheduler,
  isMemberAvailabilityEnabled
}) => {
  const scheduledMembers = schedule?.scheduledMembers || [];

  const {
    assignMemberToShift,
    loading: assignMemberLoading
  } = useAssignMemberToShift();

  const [isOpen, setIsOpen] = useState(false);
  const [modalId, setModalId] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);

  const isWeeklyView = !!(selectedDayIndex === null);
  const isDailyView = !isWeeklyView;

  const openShiftModal = i => {
    setModalId(i);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    countDraftShifts(schedule?.draftShiftIds?.length);
    setDraftShiftIds(schedule?.draftShiftIds);
  }, [schedule?.draftShiftIds]);

  const handlePressAvailabilityBadge = columnIndex => {
    setSelectedDayIndex(columnIndex);
  };

  return (
    <>
      <StyledScheduleHead>
        {!isProviderScheduler && (
          <SearchInput
            schedule={schedule}
            setHomeVenueIds={setHomeVenueIds}
            setRoleIds={setRoleIds}
            setSortBy={setSortBy}
            sortBy={sortBy}
            setVenueIds={setVenueIds}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
          />
        )}

        <DateHeaders
          isDailyView={isDailyView}
          startTime={startTime}
          selectedDayIndex={selectedDayIndex}
          setSelectedDayIndex={setSelectedDayIndex}
          isProviderScheduler={isProviderScheduler}
        />
      </StyledScheduleHead>

      <OpenShiftsTableRow
        selectedDayIndex={selectedDayIndex}
        setSelectedDayIndex={setSelectedDayIndex}
        scheduledMembers={scheduledMembers}
        schedule={schedule}
        startDate={startDate}
        onPressShiftCell={onPressShiftCell}
        onPressGridCell={onPressGridCell}
        assignMemberLoading={assignMemberLoading}
      />

      <StyledScheduleBody>
        {isDailyView && (
          <StyledGridHeader>
            <StyledDay>
              {moment(schedule?.days?.[selectedDayIndex])?.format(
                "dddd Do MMMM"
              )}
            </StyledDay>
            <StyledIconContainer>
              <Sun />
            </StyledIconContainer>
            <StyledIconContainer>
              <Moon />
            </StyledIconContainer>
          </StyledGridHeader>
        )}
        {scheduledMembers?.length > 0 &&
          scheduledMembers?.map((scheduledMember, i) => (
            <StyledScheduleRow key={scheduledMember?.member.id}>
              <AvatarCard
                openShiftModal={openShiftModal}
                memberIndex={i}
                member={scheduledMember?.member}
                hoursCompleted={scheduledMember?.hoursCompleted}
                isProviderScheduler={isProviderScheduler}
              />

              <CellRow
                scheduledMember={scheduledMember}
                startDate={startDate}
                schedule={schedule}
                startTime={startTime}
                onPressShiftCell={onPressShiftCell}
                onPressGridCell={onPressGridCell}
                onPressAvailabilityBadge={handlePressAvailabilityBadge}
                isMemberAvailabilityEnabled={isMemberAvailabilityEnabled}
                isWeeklyView={isWeeklyView}
                isDailyView={isDailyView}
                selectedDayIndex={selectedDayIndex}
                assignMemberToShift={assignMemberToShift}
              />
            </StyledScheduleRow>
          ))}

        {totalMembersCount > scheduledMembers?.length && (
          <StyledButtonWrapper>
            <RotaButton
              onClick={() => loadMore(scheduledMembers.length, LOAD_MORE_STEP)}
              size={"medium"}
            >
              Load More
            </RotaButton>
          </StyledButtonWrapper>
        )}
      </StyledScheduleBody>

      <FooterLegend />

      {scheduledMembers?.length === 0 && (
        <div style={styles.emptyRowContainer}>
          There are no shifts for this week. Create one to get started.
        </div>
      )}

      <ViewEditMember
        isOpen={isOpen}
        memberId={scheduledMembers?.[modalId]?.member?.id}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};
const mapStateToProps = s => ({
  showCancelledShifts: s.scheduler?.showCancelledShifts,
  countOfDraftShifts: s.scheduler?.countOfDraftShifts,
  draftShiftIds: s.scheduler?.draftShiftIds
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      countDraftShifts,
      setDraftShiftIds
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SchedulerGrid);
